/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from 'react'
import { Input, Select, Form, Row, Col, Typography } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import creditCardType from 'credit-card-type'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import colors from '@sportninja/common/constants/appColors'
import { Country } from '@sportninja/common/hooks/useCountries'
import useCountryDetection from '@sportninja/web/src/hooks/useCountryDetection'

const { Option } = Select
const { Title } = Typography

// Card brand logos
const CARD_LOGOS = {
  visa: 'https://js.stripe.com/v3/fingerprinted/img/visa-729c05c240c4bdb47b03ac81d9945bfe.svg',
  mastercard:
    'https://js.stripe.com/v3/fingerprinted/img/mastercard-4d8844094130711885b5e41b28c9848f.svg',
  amex: 'https://js.stripe.com/v3/fingerprinted/img/amex-a49b82f46c5cd6a96a6e418a6ca1717c.svg',
  discover:
    'https://js.stripe.com/v3/fingerprinted/img/discover-ac52cd46f89fa40a29a0bfb954e33173.svg',
}

// Emotion CSS styles
const cardInputWrapperStyle = css`
  margin-bottom: 16px;
  width: 100%;
`

const inputBaseStyle = css`
  background-color: ${colors.SECONDARY_200};
  color: ${colors.NEUTRAL_0};
  border-radius: 8px;
  height: 48px;
  padding: 0 16px;
  font-size: 16px;
  border: 1px solid ${colors.SECONDARY_200};

  &:focus,
  &:hover {
    border-color: ${colors.PRIMARY};
  }

  &::placeholder {
    color: ${colors.NEUTRAL_200};
  }
`

// Styled components using Emotion
const CardNumberInput = styled(Input)<{ hasError?: boolean }>`
  ${inputBaseStyle}
  ${(props) =>
    props.hasError &&
    `
    border-color: ${colors.ERROR_200} !important;
  `}
`

const CardDetailInput = styled(Input)<{ hasError?: boolean }>`
  ${inputBaseStyle}
  ${(props) =>
    props.hasError &&
    `
    border-color: ${colors.ERROR_200} !important;
  `}
`

const CountrySelect = styled(Select)`
  width: 100%;
  .ant-select-selector {
    background-color: ${colors.SECONDARY_200} !important;
    color: ${colors.NEUTRAL_0} !important;
    border-radius: 8px !important;
    height: 48px !important;
    padding: 0 16px !important;
    border: 1px solid ${colors.SECONDARY_200} !important;
    display: flex;
    align-items: center;
    width: 100%;
  }

  .ant-select-selection-item {
    color: ${colors.NEUTRAL_0};
    font-size: 16px;
  }

  .ant-select-arrow {
    color: ${colors.NEUTRAL_0};
    align-self: center;
  }

  .ant-select-search-input {
    color: ${colors.NEUTRAL_0} !important;
  }

  .ant-select-item {
    color: ${colors.NEUTRAL_0};
    background-color: ${colors.SECONDARY_200};
  }

  .ant-select-item-option-active,
  .ant-select-item-option-selected {
    background-color: ${colors.SECONDARY_100} !important;
  }

  .ant-select-dropdown {
    background-color: ${colors.SECONDARY_200};
    border-radius: 8px;
  }
`

const cardBrandLogoStyle = css`
  display: flex;
  gap: 8px;
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  @media (max-width: 768px) {
    display: none;
  }
`

const cardBrandLogoStyleSingleCard = css`
  display: flex;
  gap: 8px;
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  @media (max-width: 768px) {
    right: 8px;
  }
`

const cardLogoImageStyle = css`
  height: 24px;
  width: auto;
`

const InputLabel = styled(Title)`
  margin-bottom: 8px !important;
  font-size: 12px !important;
  line-height: 145% !important;
  letter-spacing: 0px !important;
  font-weight: 400 !important;
  color: ${colors.NEUTRAL_0};
`

const disclaimerTextStyle = css`
  color: ${colors.NEUTRAL_100};
  font-size: 12px;
  margin-top: 16px;
`

const errorLabelStyle = (hasError: boolean) => css`
  color: ${colors.ERROR_200};
  font-size: 12px;
  margin-top: 4px;
  min-height: ${hasError ? '16px' : '0'};
  display: ${hasError ? 'block' : 'none'};
`

export interface CreditCardInputProps {
  onChange?: (values: CreditCardValues) => void
  companyName?: string
  countries?: Country[]
}

export interface CreditCardValues {
  cardNumber: string
  expiryDate: string
  cvc: string
  country: string
  cardType?: string
  errors?: {
    expiryDate?: string
    cardNumber?: string
    cvc?: string
  }
}

const CreditCardInput: React.FC<CreditCardInputProps> = ({
  onChange,
  companyName = 'SportNinja',
  countries = [],
}) => {
  const [cardNumber, setCardNumber] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [cvc, setCvc] = useState('')
  const [country, setCountry] = useState('Canada')
  const [cardType, setCardType] = useState<string | null>(null)
  const [expiryDateError, setExpiryDateError] = useState<string | null>(null)
  const [cardNumberError, setCardNumberError] = useState<string | null>(null)
  const [cvcError, setCvcError] = useState<string | null>(null)
  const [form] = Form.useForm()

  // Detect user's country
  const detectedCountryCode = useCountryDetection()

  // Fallback countries list when none are provided
  const fallbackCountries: Country[] = [
    {
      id: 1,
      name: 'Canada',
      name_full: 'Canada',
      iso_3166_2: 'CA',
      iso_3166_3: 'CAN',
      capital: 'Ottawa',
      citizenship: 'Canadian',
      currency_symbol: 'CAD',
      is_visible: true,
    },
    {
      id: 2,
      name: 'United States',
      name_full: 'United States of America',
      iso_3166_2: 'US',
      iso_3166_3: 'USA',
      capital: 'Washington D.C.',
      citizenship: 'American',
      currency_symbol: 'USD',
      is_visible: true,
    },
    {
      id: 3,
      name: 'Brazil',
      name_full: 'Federative Republic of Brazil',
      iso_3166_2: 'BR',
      iso_3166_3: 'BRA',
      capital: 'Brasília',
      citizenship: 'Brazilian',
      currency_symbol: 'BRL',
      is_visible: true,
    },
    {
      id: 4,
      name: 'Mexico',
      name_full: 'United Mexican States',
      iso_3166_2: 'MX',
      iso_3166_3: 'MEX',
      capital: 'Mexico City',
      citizenship: 'Mexican',
      currency_symbol: 'MXN',
      is_visible: true,
    },
    {
      id: 5,
      name: 'Argentina',
      name_full: 'Argentine Republic',
      iso_3166_2: 'AR',
      iso_3166_3: 'ARG',
      capital: 'Buenos Aires',
      citizenship: 'Argentine',
      currency_symbol: 'ARS',
      is_visible: true,
    },
    {
      id: 6,
      name: 'Chile',
      name_full: 'Republic of Chile',
      iso_3166_2: 'CL',
      iso_3166_3: 'CHL',
      capital: 'Santiago',
      citizenship: 'Chilean',
      currency_symbol: 'CLP',
      is_visible: true,
    },
    {
      id: 7,
      name: 'Colombia',
      name_full: 'Republic of Colombia',
      iso_3166_2: 'CO',
      iso_3166_3: 'COL',
      capital: 'Bogotá',
      citizenship: 'Colombian',
      currency_symbol: 'COP',
      is_visible: true,
    },
    {
      id: 8,
      name: 'Peru',
      name_full: 'Republic of Peru',
      iso_3166_2: 'PE',
      iso_3166_3: 'PER',
      capital: 'Lima',
      citizenship: 'Peruvian',
      currency_symbol: 'PEN',
      is_visible: true,
    },
  ]

  // Use provided countries or fallback if empty
  const countryOptions = countries.length > 0 ? countries : fallbackCountries

  // Set country based on detected country code
  useEffect(() => {
    if (detectedCountryCode && countryOptions.length > 0) {
      const matchedCountry = countryOptions.find(
        (c) => c.iso_3166_2 === detectedCountryCode
      )
      if (matchedCountry) {
        setCountry(matchedCountry.name)
      }
    }
  }, [detectedCountryCode, countryOptions])

  // Format card number with spaces
  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '')
    const matches = v.match(/\d{4,16}/g)
    const match = (matches && matches[0]) || ''
    const parts = []
    for (let i = 0, len = match.length; i < len; i += 4) {
      // @ts-ignore
      parts.push(match.substring(i, i + 4))
    }

    if (parts.length) {
      return parts.join(' ')
    } else {
      return value
    }
  }

  // Format expiry date (MM / YY)
  const formatExpiryDate = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '')

    if (v.length <= 2) {
      return v
    }

    return `${v.substring(0, 2)} / ${v.substring(2, 4)}`
  }

  // Validate card number
  const validateCardNumber = (value: string) => {
    if (!value) {
      return 'Card number is required'
    }

    const cleanNumber = value.replace(/\s+/g, '')

    // Check if it's a valid length
    if (cleanNumber.length < 13 || cleanNumber.length > 19) {
      return 'Card Number Must Be Between 13 And 19 Digits'
    }

    // Check if it passes Luhn algorithm (basic card number validation)
    let sum = 0
    let shouldDouble = false

    // Loop through values starting from the rightmost digit
    for (let i = cleanNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cleanNumber.charAt(i))

      if (shouldDouble) {
        digit *= 2
        if (digit > 9) {
          digit -= 9
        }
      }

      sum += digit
      shouldDouble = !shouldDouble
    }

    if (sum % 10 !== 0) {
      return 'Invalid Card Number'
    }

    // Check if it's a recognized card type
    const cardTypes = creditCardType(cleanNumber) as any[]
    if (cardTypes.length === 0) {
      return 'Unrecognized Card Type'
    }

    return null
  }

  // Validate CVC
  const validateCVC = (value: string, cardTypeValue: string | null) => {
    if (!value) {
      return 'Security code is required'
    }

    // Different card types have different CVC lengths
    if (cardTypeValue === 'american-express') {
      if (value.length !== 4) {
        return 'American Express Security Code Must Be 4 Digits'
      }
    } else {
      if (value.length !== 3) {
        return 'Security Code Must Be 3 Digits'
      }
    }

    return null
  }

  // Validate expiry date
  const validateExpiryDate = (value: string) => {
    if (!value) {
      return 'Expiration Date Is Required'
    }

    if (value.length < 7) {
      return 'Please Enter A Complete Expiration Date'
    }

    const parts = value.split(' / ')
    if (parts.length !== 2) {
      return 'Invalid Expiration Date Format'
    }

    const month = parseInt(parts[0], 10)
    const year = parseInt(`20${parts[1]}`, 10)

    if (isNaN(month) || isNaN(year)) {
      return 'Invalid Expiration Date'
    }

    if (month < 1 || month > 12) {
      return 'Invalid Month'
    }

    const currentDate = new Date()
    const currentYear = currentDate.getFullYear()
    const currentMonth = currentDate.getMonth() + 1 // JavaScript months are 0-indexed

    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      return 'Expiration Date Cannot Be In The Past'
    }

    return null
  }

  // Detect card type based on number
  useEffect(() => {
    if (cardNumber.length > 4) {
      const cleanNumber = cardNumber.replace(/\s+/g, '')
      // Use any type for now since the library doesn't provide proper TypeScript definitions
      const cardTypes = creditCardType(cleanNumber) as any[]

      if (cardTypes.length > 0 && cardTypes[0].type) {
        setCardType(cardTypes[0].type)
      } else {
        setCardType(null)
      }
    } else {
      setCardType(null)
    }

    // Validate card number when it changes
    if (cardNumber) {
      const error = validateCardNumber(cardNumber)
      setCardNumberError(error)
    } else {
      setCardNumberError(null)
    }
  }, [cardNumber])

  // Validate expiry date when it changes
  useEffect(() => {
    if (expiryDate && expiryDate?.length > 0) {
      const error = validateExpiryDate(expiryDate)
      setExpiryDateError(error)
    } else {
      setExpiryDateError(null)
    }
  }, [expiryDate])

  // Validate CVC when it changes or card type changes
  useEffect(() => {
    if (cvc) {
      const error = validateCVC(cvc, cardType)
      setCvcError(error)
    } else {
      setCvcError(null)
    }
  }, [cvc, cardType])

  // Update parent component with values
  useEffect(() => {
    if (onChange) {
      onChange({
        cardNumber,
        expiryDate,
        cvc,
        country,
        cardType: cardType || undefined,
        errors: {
          expiryDate: expiryDateError || undefined,
          cardNumber: cardNumberError || undefined,
          cvc: cvcError || undefined,
        },
      })
    }
  }, [
    cardNumber,
    expiryDate,
    cvc,
    country,
    cardType,
    expiryDateError,
    cardNumberError,
    cvcError,
  ])

  // Handle card number change
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const formattedValue = formatCardNumber(value)
    setCardNumber(formattedValue)
  }

  // Handle expiry date change
  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const formattedValue = formatExpiryDate(value)
    setExpiryDate(formattedValue)
  }

  // Get card logo based on type
  const getCardLogo = () => {
    if (!cardType) {
      return null
    }

    switch (cardType) {
      case 'visa':
        return CARD_LOGOS.visa
      case 'mastercard':
        return CARD_LOGOS.mastercard
      case 'american-express':
        return CARD_LOGOS.amex
      case 'discover':
        return CARD_LOGOS.discover
      default:
        return null
    }
  }

  // Render card brand logos
  const renderCardLogos = () => {
    if (cardType) {
      const logo = getCardLogo()
      if (logo) {
        return (
          <div css={cardBrandLogoStyleSingleCard}>
            <img css={cardLogoImageStyle} src={logo} alt={cardType} />
          </div>
        )
      }
    }

    // Show all logos when no card is detected
    return (
      <div css={cardBrandLogoStyle}>
        <img css={cardLogoImageStyle} src={CARD_LOGOS.visa} alt='Visa' />
        <img
          css={cardLogoImageStyle}
          src={CARD_LOGOS.mastercard}
          alt='Mastercard'
        />
        <img
          css={cardLogoImageStyle}
          src={CARD_LOGOS.amex}
          alt='American Express'
        />
        <img
          css={cardLogoImageStyle}
          src={CARD_LOGOS.discover}
          alt='Discover'
        />
      </div>
    )
  }

  return (
    <Form form={form} layout='vertical'>
      <div css={cardInputWrapperStyle}>
        <InputLabel level={1}>Card Number</InputLabel>
        <div style={{ position: 'relative' }}>
          <CardNumberInput
            placeholder='1234 1234 1234 1234'
            value={cardNumber}
            onChange={handleCardNumberChange}
            maxLength={19}
            hasError={!!cardNumberError}
          />
          {renderCardLogos()}
        </div>
        <div css={errorLabelStyle(!!cardNumberError)}>{cardNumberError}</div>
      </div>

      <Row gutter={16}>
        <Col span={12}>
          <div css={cardInputWrapperStyle}>
            <InputLabel level={5}>Expiration Date</InputLabel>
            <CardDetailInput
              placeholder='MM / YY'
              value={expiryDate}
              onChange={handleExpiryDateChange}
              maxLength={7}
              hasError={!!expiryDateError}
            />
            <div css={errorLabelStyle(!!expiryDateError)}>
              {expiryDateError}
            </div>
          </div>
        </Col>
        <Col span={12}>
          <div css={cardInputWrapperStyle}>
            <InputLabel level={5}>Security Code</InputLabel>
            <CardDetailInput
              placeholder='CVC'
              value={cvc}
              onChange={(e) => setCvc(e.target.value)}
              maxLength={4}
              hasError={!!cvcError}
            />
            <div css={errorLabelStyle(!!cvcError)}>{cvcError}</div>
          </div>
        </Col>
      </Row>

      <div css={cardInputWrapperStyle}>
        <InputLabel level={5}>Country</InputLabel>
        <CountrySelect
          value={country}
          onChange={(value: unknown) => setCountry(value as string)}
          suffixIcon={<DownOutlined style={{ color: 'white' }} />}
          showSearch
          filterOption={(input, option) => {
            if (!option || option.children === undefined) {
              return false
            }
            return option.children
              .toString()
              .toLowerCase()
              .includes(input.toLowerCase())
          }}
          placeholder='Select or search for a country'
        >
          {countryOptions.map((country) => (
            <Option key={country.iso_3166_2} value={country.name}>
              {country.name}
            </Option>
          ))}
        </CountrySelect>
      </div>

      <p css={disclaimerTextStyle}>
        By providing your card information, you allow {companyName} to charge
        your card for future payments in accordance with their terms.
      </p>
    </Form>
  )
}

export default CreditCardInput
